import axios from 'axios'
import { directoryApiClient } from './api/auth'
import { logEvent } from './datadog'
import { getAccessToken, saveTokens } from './authentication'

const WEBAUTHN_CREDENTIAL_ID_KEY = 'webauthn_credential_id'

const getStoredCredentialId = (): string | null => {
  try {
    const id = localStorage.getItem(WEBAUTHN_CREDENTIAL_ID_KEY)
    return id || null
  } catch {
    return null
  }
}

const setStoredCredentialId = (id: string | null): void => {
  try {
    if (id) {
      localStorage.setItem(WEBAUTHN_CREDENTIAL_ID_KEY, id)
    } else {
      localStorage.removeItem(WEBAUTHN_CREDENTIAL_ID_KEY)
    }
  } catch {
    // ignore
  }
}

export const removeStoredCredentialId = (id: string): void => {
  if (getStoredCredentialId() === id) {
    setStoredCredentialId(null)
  }
}

export class SsoForbiddenError extends Error {
  constructor() {
    super('Passkeys are not available for SSO accounts')
    this.name = 'SsoForbiddenError'
  }
}

export type WebAuthnCredential = {
  id: string
  device_nickname: string | null
  last_used_at: string | null
  created_at: string
  icon_dark: string | null
  icon_light: string | null
}

type ServerRegistrationOptions = Omit<PublicKeyCredentialCreationOptions, 'user' | 'excludeCredentials'> & {
  user: Omit<PublicKeyCredentialUserEntity, 'id'> & { id: string }
  excludeCredentials?: Array<Omit<PublicKeyCredentialDescriptor, 'id'> & { id: string }>
}

type RegistrationChallengeResponse = {
  challenge: string
  options: ServerRegistrationOptions
}

type AuthenticationChallengeResponse = {
  challenge_id: string
  challenge: string
  options: PublicKeyCredentialRequestOptions
}

type AuthenticationTokens = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
  created_at: number
}

/**
 * Check if WebAuthn is supported by the browser
 */
export const isWebAuthnSupported = (): boolean => {
  return (
    window?.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  )
}

/**
 * Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
 */
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) {
    return false
  }

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch (error) {
    logEvent('webauthn_platform_check_error', undefined, { error: String(error) })
    return false
  }
}

/**
 * Helper to convert base64url to Uint8Array
 */
const base64UrlToUint8Array = (base64url: string): BufferSource => {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}

/**
 * Helper to convert Uint8Array to base64url
 */
const uint8ArrayToBase64Url = (buffer: Uint8Array): string => {
  const base64 = window.btoa(String.fromCharCode.apply(null, Array.from(buffer)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Register a new passkey for the current user
 */
export const registerPasskey = async (deviceNickname?: string): Promise<void> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  // Step 1: Get registration challenge from server
  const challengeResponse = await directoryApiClient.post<RegistrationChallengeResponse>(
    '/api/auth/webauthn/registrations/challenges'
  )

  const { challenge, options } = challengeResponse.data

  // Step 2: Prepare credential creation options
  const credentialCreationOptions: CredentialCreationOptions = {
    publicKey: {
      ...options,
      challenge: base64UrlToUint8Array(challenge),
      user: {
        ...options.user,
        id: base64UrlToUint8Array(options.user.id),
      },
      excludeCredentials: options.excludeCredentials?.map((cred) => ({
        ...cred,
        id: base64UrlToUint8Array(cred.id),
      })),
    },
  }

  // Step 3: Create credential via platform authenticator
  logEvent('webauthn_registration_start')
  const credential = await navigator.credentials.create(credentialCreationOptions)

  if (!credential || credential.type !== 'public-key') {
    logEvent('webauthn_registration_failed', undefined, { reason: 'invalid_credential_type' })
    throw new Error('Failed to create credential')
  }

  const pkCredential = credential as PublicKeyCredential
  const response = pkCredential.response as AuthenticatorAttestationResponse

  // Step 4: Send attestation to server for verification
  const verifyResponse = await directoryApiClient.post<{ id: string }>(
    '/api/auth/webauthn/registrations/verifications',
    {
      id: pkCredential.id,
      rawId: uint8ArrayToBase64Url(new Uint8Array(pkCredential.rawId)),
      type: pkCredential.type,
      response: {
        clientDataJSON: uint8ArrayToBase64Url(new Uint8Array(response.clientDataJSON)),
        attestationObject: uint8ArrayToBase64Url(new Uint8Array(response.attestationObject)),
      },
      device_nickname: deviceNickname,
    }
  )

  setStoredCredentialId(verifyResponse.data.id)
  logEvent('webauthn_registration_success')
}

/**
 * Authenticate with a passkey
 */
export const authenticateWithPasskey = async (): Promise<AuthenticationTokens> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  // Step 1: Get authentication challenge from server
  const challengeResponse = await directoryApiClient.post<AuthenticationChallengeResponse>(
    '/api/auth/webauthn/authentications/challenges'
  )

  const { challenge_id, challenge, options } = challengeResponse.data

  // Step 2: Prepare credential request options
  const credentialRequestOptions: CredentialRequestOptions = {
    publicKey: {
      ...options,
      challenge: base64UrlToUint8Array(challenge),
      allowCredentials: options.allowCredentials?.map((cred) => ({
        ...cred,
        id: base64UrlToUint8Array(cred.id as unknown as string),
      })),
    },
  }

  // Step 3: Get assertion from platform authenticator
  logEvent('webauthn_authentication_start')
  const credential = await navigator.credentials.get(credentialRequestOptions)

  if (!credential || credential.type !== 'public-key') {
    logEvent('webauthn_authentication_failed', undefined, { reason: 'invalid_credential_type' })
    throw new Error('Failed to authenticate')
  }

  const pkCredential = credential as PublicKeyCredential
  const response = pkCredential.response as AuthenticatorAssertionResponse

  // Step 4: Send assertion to server for verification
  let verifyResponse
  try {
    verifyResponse = await directoryApiClient.post<AuthenticationTokens>(
      '/api/auth/webauthn/authentications/verifications',
      {
        challenge_id,
        id: pkCredential.id,
        rawId: uint8ArrayToBase64Url(new Uint8Array(pkCredential.rawId)),
        type: pkCredential.type,
        response: {
          clientDataJSON: uint8ArrayToBase64Url(new Uint8Array(response.clientDataJSON)),
          authenticatorData: uint8ArrayToBase64Url(new Uint8Array(response.authenticatorData)),
          signature: uint8ArrayToBase64Url(new Uint8Array(response.signature)),
          userHandle: response.userHandle
            ? uint8ArrayToBase64Url(new Uint8Array(response.userHandle))
            : undefined,
        },
      }
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error === 'sso_forbidden') {
      throw new SsoForbiddenError()
    }
    throw error
  }

  // Step 5: Save tokens to cookies
  const tokens = verifyResponse.data
  saveTokens(tokens.access_token, tokens.refresh_token)

  try {
    await syncStoredCredentialIds()
  } catch (error) {
    logEvent('webauthn_sync_credential_ids_error', undefined, { error: String(error) }, error instanceof Error ? error : new Error(String(error)), 'warn')
  }
  logEvent('webauthn_authentication_success')
  return tokens
}

/**
 * List all passkeys for the current user
 */
export const listPasskeys = async (): Promise<WebAuthnCredential[]> => {
  const accessToken = getAccessToken()
  const response = await directoryApiClient.get<{ credentials: WebAuthnCredential[] }>(
    '/api/auth/webauthn/credentials',
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
  return response.data.credentials
}

export const syncStoredCredentialIds = async (): Promise<void> => {
  const credentials = await listPasskeys()
  if (credentials.length === 0) {
    setStoredCredentialId(null)
    return
  }
  const currentId = getStoredCredentialId()
  const stillExists = currentId && credentials.some((c) => c.id === currentId)
  if (!stillExists) {
    setStoredCredentialId(credentials[0].id)
  }
}

/**
 * Rename a passkey
 */
export const renamePasskey = async (credentialId: string, newName: string): Promise<void> => {
  const accessToken = getAccessToken()
  await directoryApiClient.patch(
    `/api/auth/webauthn/credentials/${credentialId}`,
    { device_nickname: newName },
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  )
}

/**
 * Delete a passkey
 */
export const deletePasskey = async (credentialId: string): Promise<void> => {
  const accessToken = getAccessToken()
  await directoryApiClient.delete(`/api/auth/webauthn/credentials/${credentialId}`, {
    ...(accessToken && { headers: { Authorization: `Bearer ${accessToken}` } }),
  })
  removeStoredCredentialId(credentialId)
  logEvent('webauthn_credential_deleted')
}

export const hasRegisteredPasskey = (): boolean => {
  return getStoredCredentialId() !== null
}

export const isConditionalMediationAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false

  try {
    return typeof PublicKeyCredential.isConditionalMediationAvailable === 'function'
      && await PublicKeyCredential.isConditionalMediationAvailable()
  } catch {
    return false
  }
}

const requestAndVerifyPasskey = async (
  credentialRequestOptions: CredentialRequestOptions,
  challengeId: string,
): Promise<AuthenticationTokens> => {
  const credential = await navigator.credentials.get(credentialRequestOptions)

  if (!credential || credential.type !== 'public-key') {
    throw new Error('Failed to authenticate')
  }

  const pkCredential = credential as PublicKeyCredential
  const response = pkCredential.response as AuthenticatorAssertionResponse

  let verifyResponse
  try {
    verifyResponse = await directoryApiClient.post<AuthenticationTokens>(
      '/api/auth/webauthn/authentications/verifications',
      {
        challenge_id: challengeId,
        id: pkCredential.id,
        rawId: uint8ArrayToBase64Url(new Uint8Array(pkCredential.rawId)),
        type: pkCredential.type,
        response: {
          clientDataJSON: uint8ArrayToBase64Url(new Uint8Array(response.clientDataJSON)),
          authenticatorData: uint8ArrayToBase64Url(new Uint8Array(response.authenticatorData)),
          signature: uint8ArrayToBase64Url(new Uint8Array(response.signature)),
          userHandle: response.userHandle
            ? uint8ArrayToBase64Url(new Uint8Array(response.userHandle))
            : undefined,
        },
      }
    )
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error === 'sso_forbidden') {
      throw new SsoForbiddenError()
    }
    throw error
  }

  const tokens = verifyResponse.data
  saveTokens(tokens.access_token, tokens.refresh_token)
  syncStoredCredentialIds().catch(() => {})
  return tokens
}

export const authenticateWithConditionalUI = async (
  abortController: AbortController,
): Promise<AuthenticationTokens> => {
  const challengeResponse = await directoryApiClient.post<AuthenticationChallengeResponse>(
    '/api/auth/webauthn/authentications/challenges'
  )
  const { challenge_id, challenge, options } = challengeResponse.data

  logEvent('webauthn_conditional_ui_start')
  const tokens = await requestAndVerifyPasskey(
    {
      publicKey: {
        ...options,
        challenge: base64UrlToUint8Array(challenge),
        allowCredentials: [],
      },
      signal: abortController.signal,
      mediation: 'conditional' as CredentialMediationRequirement,
    },
    challenge_id,
  )
  logEvent('webauthn_conditional_ui_success')
  return tokens
}

export const webAuthnService = {
  isSupported: isWebAuthnSupported,
  isConditionalMediationAvailable,
  authenticateWithConditionalUI,
  registerPasskey,
  authenticateWithPasskey,
  listPasskeys,
  renamePasskey,
  deletePasskey,
}
